import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { Alert, AlertDescription } from '../ui/alert'
import { Button } from '../ui/button'

interface Props {
    org: string
    importUid: string
    extractionStartedAt?: string | null
    extractionTier?: number | null
    awaitingExtraction: number
    onChange: () => void
}

/**
 * The two steps of an import: documents are read and typed first, then
 * extracted once the go is given, one tier of document types at a time
 * (leases, plans, contracts and diagnostics, reports and DOE inventories, the
 * rest). Shows the go button while the import waits, the tier being extracted
 * after it, nothing once every tier is out.
 */
export function ImportExtractionStep({ org, importUid, extractionStartedAt, extractionTier, awaitingExtraction, onChange }: Props) {
    const { t } = useTranslation('imports')
    const [failed, setFailed] = useState(false)
    const start = $api.useMutation('post', '/api/v1/organizations/{orgCode}/imports/{importUid}/start-extraction')

    if (!extractionStartedAt) {
        const go = async () => {
            setFailed(false)
            try {
                await start.mutateAsync({ params: { path: { orgCode: org, importUid } } })
                onChange()
            } catch {
                setFailed(true)
            }
        }
        return (
            <Alert data-testid="import-extraction-waiting">
                <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
                    <span data-testid="import-extraction-waiting-text">
                        {t('extraction.waiting', { count: awaitingExtraction })}
                        {failed && <span className="ml-2 text-destructive">{t('actions.error')}</span>}
                    </span>
                    <Button size="sm" onClick={() => void go()} disabled={start.isPending} data-testid="import-start-extraction">
                        {t('extraction.start')}
                    </Button>
                </AlertDescription>
            </Alert>
        )
    }
    if (extractionTier === null || extractionTier === undefined) {
        return null
    }
    return (
        <Alert data-testid="import-extraction-running">
            <AlertDescription data-testid="import-extraction-tier">
                {t('extraction.running', { tier: Math.max(1, extractionTier), label: t(`extraction.tiers.${Math.max(1, extractionTier)}`) })}
            </AlertDescription>
        </Alert>
    )
}
